import type { IAudienceHeartbeatAggregate } from './playback-heartbeat'

/** True when the aggregate includes at least one score the server may persist (motion, presence, or person-CV metrics). */
export function audienceHeartbeatAggregateHasPersistableMetric(
  aggregate: IAudienceHeartbeatAggregate | null | undefined
): boolean {
  if (aggregate == null) {
    return false
  }
  return (
    aggregate.motionScore != null
    || aggregate.presenceScore != null
    || (aggregate.personQualifiedTrackCount != null && aggregate.personQualifiedTrackCount > 0)
    || (aggregate.personDwellSeconds != null && aggregate.personDwellSeconds > 0)
  )
}

/**
 * Drops person-CV fields when the tenant has not enabled `audiencePersonCvAnalytics` (super-admin only).
 * Call after Zod parse, before forward/persist checks.
 */
export function stripAudienceCvFieldsUnlessTenantAllows(
  aggregate: IAudienceHeartbeatAggregate,
  audiencePersonCvAnalyticsEnabled: boolean
): IAudienceHeartbeatAggregate {
  if (audiencePersonCvAnalyticsEnabled) {
    return aggregate
  }
  const {
    cvPersonModelId: _omitCvId,
    personQualifiedTrackCount: _omitQ,
    personDwellSeconds: _omitD,
    ...rest
  } = aggregate
  return rest
}

/**
 * API (players route): include parsed `audienceAggregate` in the body forwarded to TenantDO only when
 * the tenant allows audience analytics and the client sent a non-empty aggregate shape worth evaluating.
 */
export function shouldForwardAudienceAggregateToTenantDO(
  tenantAudienceAnalyticsEnabled: boolean,
  aggregate: IAudienceHeartbeatAggregate | null | undefined
): boolean {
  return tenantAudienceAnalyticsEnabled && aggregate != null && audienceHeartbeatAggregateHasPersistableMetric(aggregate)
}

/**
 * TenantDO: insert `player_audience_sample` only when the player opted in and the aggregate carries a metric.
 */
export function shouldPersistAudienceSampleFromHeartbeat(
  playerAudienceAnalyticsEnabled: boolean | null | undefined,
  aggregate: IAudienceHeartbeatAggregate | null | undefined
): boolean {
  return playerAudienceAnalyticsEnabled === true && audienceHeartbeatAggregateHasPersistableMetric(aggregate)
}

/**
 * Player: send `audienceAggregate` on the heartbeat only when tenant + player allow, memory is OK, and the
 * pending aggregate has a score (avoids useless traffic for interval-only payloads).
 */
export function shouldSendAudienceAggregateOnPlayerHeartbeat(
  tenantAudienceAnalytics: boolean | undefined,
  playerAudienceAnalyticsEnabled: boolean,
  lowMemoryMode: boolean,
  aggregate: IAudienceHeartbeatAggregate | null | undefined
): boolean {
  return (
    tenantAudienceAnalytics === true
    && playerAudienceAnalyticsEnabled
    && !lowMemoryMode
    && audienceHeartbeatAggregateHasPersistableMetric(aggregate)
  )
}
